import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
    Button,
    HiddenInput,
    ModalComponent,
    notify,
    TextField,
} from '@speeki/global-ui-components';
import { ModalProps } from '@speeki/global-ui-components';
import { Modal } from '@speeki/global-ui-components';

import { UpdateRecoveryEmailDocument } from '@graphql/generated/graphql';

import { useUserOptions } from '@utils/hooks/useUserOptions';

import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { emailValidation } from '@shared/validations';

interface MfaEmailModalProps extends Pick<ModalProps, 'onClose' | 'open'> {}

export const MfaEmailModal = ({ onClose, open }: MfaEmailModalProps) => {
    const { t } = useTranslation();
    const { data: userOptions } = useUserOptions();

    const [changeRecoveryEmail, { loading }] = useMutation(
        UpdateRecoveryEmailDocument,
        {
            context: {
                notifyOnError: true,
            },
            onCompleted: () => {
                notify(t('SECONDARY_EMAIL_HAS_BEEN_SAVED')).SUCCESS();
            },
            onError: () => {},
        },
    );

    const {
        formState: { errors, isDirty, isSubmitted, isValid },
        handleSubmit,
        register,
        reset,
    } = useForm<MfaFormValues>({
        mode: 'onChange',
        resolver: yupResolver(MfaFromSchema),
    });

    const handleChangeEmail = (data: MfaFormValues) => {
        void changeRecoveryEmail({
            onCompleted: onClose,
            variables: { input: { newRecoveryEmail: data.email } },
        });
    };

    const handleCancel = () => {
        reset();
        onClose();
    };

    return (
        <ModalComponent closeButton onClose={onClose} open={open} width={648}>
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('MFA_ADDITIONAL_VERIFICATION')}
                    </Modal.TitleSecondary>
                    <Modal.Description>
                        {t('MFA_ADDITIONAL_VERIFICATION_TEXT')}
                    </Modal.Description>
                </Modal.Header>

                <HiddenInput
                    input={
                        <TextField
                            {...register('email')}
                            className="htmlToImageIgnore"
                            error={!!errors.email}
                            label={t('SECONDARY_EMAIL_ADDRESS')}
                            message={t(errors.email?.message ?? '')}
                            placeholder={t('ENTER_NEW_SECONDARY_EMAIL')}
                            testId="2DHDnND"
                            type="email"
                        />
                    }
                    label={t('SECONDARY_EMAIL_ADDRESS')}
                    testId="o0okTOS"
                    value={userOptions?.user.recoveryEmail ?? ''}
                />
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={handleCancel}
                        testId="button_cancel"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={
                            loading || (!isValid && isSubmitted) || !isDirty
                        }
                        isLoading={loading}
                        onClick={handleSubmit(handleChangeEmail)}
                        testId="button_save"
                        variant={'primary'}
                    >
                        {t('SAVE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};

const MfaFromSchema = yup.object({
    email: emailValidation,
});

type MfaFormValues = yup.InferType<typeof MfaFromSchema>;
